/**
 * useDisplayOptions — localStorage-backed display preferences for the agent ChatPanel
 *
 * Controls visibility of reasoning blocks, tool details, model info,
 * timestamps, and code wrapping. Used by DisplayOptionsMenu and
 * threaded down through MessageList → MessageBubble → ToolCallBlock → BashOutput.
 */
import { useState, useCallback } from 'react'

const STORAGE_KEY = 'papan-agent-display-options'

/**
 * One-time reset marker for a DEFAULT that changed after people already had
 * options stored.
 *
 * `save()` writes the WHOLE object, so someone who once toggled timestamps has
 * `showReasoning: true` persisted without ever having chosen it — and a plain
 * change to DEFAULTS would never reach them. That is the difference between
 * "off by default" and "off for new browsers only", and it is exactly the case
 * a reviewer opens the app to check.
 *
 * Bump the suffix when a default changes this way, and apply it in `load()` to
 * that key alone — never by clearing STORAGE_KEY, which would also discard the
 * preferences people did choose.
 */
const DEFAULT_RESET_KEY = 'papan-agent-display-options-reasoning-off'

export interface DisplayOptions {
  showReasoning: boolean
  showToolDetails: boolean
  showModelInfo: boolean
  showTimestamps: boolean
  wrapDiffLines: boolean
  /**
   * Dev-only: reveal the tool-render preview + typography experiment menus
   * in the composer footer. The toggle is only surfaced in `import.meta.env.DEV`
   * builds; it stays at the default (false) in production and the menus it
   * gates already tree-shake to null there.
   */
  showDevTools: boolean
}

const DEFAULTS: DisplayOptions = {
  /** OFF. The model's thinking is detail most people do not want by default;
   *  the "Reasoning" switch in the chat's display menu turns it back on and
   *  that choice persists. */
  showReasoning: false,
  showToolDetails: true,
  showModelInfo: true,
  showTimestamps: false,
  wrapDiffLines: false,
  showDevTools: false,
}

function load(): DisplayOptions {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      // Mark it here too. A fresh browser has nothing to migrate, but leaving
      // the marker unset means the user's FIRST deliberate enable writes a blob
      // with no marker — and the next load then "migrates" it straight back
      // off. That is the same bug this reset exists to prevent, mirrored.
      try { localStorage.setItem(DEFAULT_RESET_KEY, '1') } catch { /* ignore */ }
      return { ...DEFAULTS }
    }
    const merged = { ...DEFAULTS, ...JSON.parse(raw) }
    // Apply the new default ONCE to anyone carrying an incidentally-saved
    // value, then never again — so a later deliberate toggle sticks.
    if (!localStorage.getItem(DEFAULT_RESET_KEY)) {
      merged.showReasoning = DEFAULTS.showReasoning
      save(merged)
      localStorage.setItem(DEFAULT_RESET_KEY, '1')
    }
    return merged
  } catch {
    return { ...DEFAULTS }
  }
}

function save(options: DisplayOptions): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(options))
  } catch { /* ignore quota errors */ }
}

export function useDisplayOptions() {
  const [options, setOptions] = useState<DisplayOptions>(load)

  const toggleOption = useCallback((key: keyof DisplayOptions) => {
    setOptions(prev => {
      const next = { ...prev, [key]: !prev[key] }
      save(next)
      return next
    })
  }, [])

  return { options, toggleOption }
}
